import { Link } from "react-router-dom";
import { Phone, Mail, Facebook, Instagram, MapPin } from "lucide-react";
import { SITE } from "@/lib/site";
import Cta from "@/components/Cta";

export default function Footer() {
  return (
    <footer className="relative border-t border-white/10 bg-ink-950 overflow-hidden" data-testid="site-footer">
      {/* Big CTA band */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-16 border-b border-white/10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <p className="overline mb-4">Ready when you are</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-white max-w-2xl">
              Let's get you a<br />connection you'll <span className="text-cyan-400 text-glow">love.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Cta to="/survey" data-testid="footer-survey-cta">Free Site Survey</Cta>
            <Cta href={`tel:${SITE.phoneRaw}`} variant="secondary" data-testid="footer-call-cta">
              <Phone size={16} /> Call Us
            </Cta>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-1">
          <img src="/brand/logo-white.png" alt="CYAN Wireless" className="h-7 w-auto" />
          <p className="text-zinc-500 text-sm mt-4 max-w-xs">
            Locally owned, blazing-fast rural internet for {SITE.areas.join(", ")}, {SITE.state}.
          </p>
        </div>

        <div>
          <h3 className="font-mono-tech text-xs uppercase tracking-widest text-cyan-400 mb-4">Explore</h3>
          <ul className="space-y-3 text-sm text-zinc-400">
            <li><Link to="/coverage" className="hover:text-cyan-400 transition-colors">Coverage Map</Link></li>
            <li><Link to="/wavfiber" className="hover:text-cyan-400 transition-colors">WAVFiber</Link></li>
            <li><Link to="/streaming" className="hover:text-cyan-400 transition-colors">Streaming</Link></li>
            <li><Link to="/wifi-consulting" className="hover:text-cyan-400 transition-colors">WiFi Consulting</Link></li>
            <li><Link to="/update" className="hover:text-cyan-400 transition-colors">Billing Update</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="font-mono-tech text-xs uppercase tracking-widest text-cyan-400 mb-4">Contact</h3>
          <ul className="space-y-3 text-sm text-zinc-400">
            <li><a href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-2 hover:text-cyan-400 transition-colors"><Phone size={14} /> {SITE.phone}</a></li>
            <li><a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-cyan-400 transition-colors"><Mail size={14} /> {SITE.email}</a></li>
            <li className="flex items-center gap-2"><MapPin size={14} /> {SITE.state}, USA</li>
          </ul>
        </div>

        <div>
          <h3 className="font-mono-tech text-xs uppercase tracking-widest text-cyan-400 mb-4">Hours</h3>
          <ul className="space-y-2 text-sm text-zinc-400">
            {SITE.hours.map((h) => (
              <li key={h.d} className="flex justify-between gap-4"><span>{h.d}</span><span className="text-zinc-500">{h.h}</span></li>
            ))}
          </ul>
          <div className="flex gap-3 mt-5">
            <a href={SITE.social.facebook} aria-label="Facebook" target="_blank" rel="noreferrer" className="w-9 h-9 grid place-items-center rounded-full border border-white/10 text-zinc-300 hover:border-cyan-500 hover:text-cyan-400 transition-colors"><Facebook size={16} /></a>
            <a href={SITE.social.instagram} aria-label="Instagram" target="_blank" rel="noreferrer" className="w-9 h-9 grid place-items-center rounded-full border border-white/10 text-zinc-300 hover:border-cyan-500 hover:text-cyan-400 transition-colors"><Instagram size={16} /></a>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <p>© {new Date().getFullYear()} {SITE.name}. All Rights Reserved.</p>
          <p className="font-mono-tech">Love Your Internet.</p>
        </div>
      </div>
    </footer>
  );
}
